/**
 * ApplicationsTable — очередь заявок: кто, город, размер, когда
 * подана, статус, кто рассмотрел. Клик по строке → карточка заявки.
 *
 * В очереди лежат заявки двух происхождений, и строка должна читаться
 * одинаково для обеих. У заявки из приложения заполнено всё, у лида с
 * лендинга — только имя и телефон. Поэтому подпись строки берётся по
 * тому, что есть (название → имя → телефон), а пустые колонки честно
 * показывают прочерк.
 *
 * Иконка следует за подписью, а не за происхождением: дом — когда в
 * подписи название клиники, человек — когда имя или один телефон.
 * Иначе у лида, который всё-таки назвал клинику, рядом с названием
 * стоял бы человечек.
 *
 * Пилюля «не подтверждён» стоит рядом с номером, а не в отдельной
 * колонке: решение «звонить или слать доступы» принимается глядя
 * именно на телефон.
 */

import {Building2, ChevronRight, User} from 'lucide-react';

import {type ApplicationItem, applicationTitle} from '@services/applications';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@components/ui/Table';

import {doctorsLabel, fmtAppDate, fmtAppPhone, orDash} from '../Applications.utils';
import {AppStatusPill} from '../AppStatusPill';

const th = 'text-[11px] font-semibold uppercase tracking-[.04em] text-skr-label';

type Props = {
  rows: ApplicationItem[];
  onOpen: (id: string) => void;
};

export const ApplicationsTable = ({rows, onOpen}: Props) => (
  <div className="overflow-x-auto">
    <Table className="min-w-[860px] text-[13.5px]">
      <TableHeader>
        <TableRow className="border-b border-skr-line2 bg-skr-head hover:bg-skr-head">
          <TableHead className={th}>Кто</TableHead>
          <TableHead className={th}>Город</TableHead>
          <TableHead className={th}>Размер</TableHead>
          <TableHead className={th}>Подана</TableHead>
          <TableHead className={th}>Статус</TableHead>
          <TableHead className={th}>Рассмотрел</TableHead>
          <TableHead className="w-9" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((a) => (
          <TableRow
            key={a.id}
            onClick={() => onOpen(a.id)}
            className="cursor-pointer border-b border-skr-hairline hover:bg-skr-head"
          >
            <TableCell>
              <div className="flex items-center gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-skr-icon-bg text-muted-foreground">
                  {a.companyName !== null ? (
                    <Building2 className="size-[17px]" strokeWidth={1.8} />
                  ) : (
                    <User className="size-[17px]" strokeWidth={1.8} />
                  )}
                </span>
                <div className="min-w-0 leading-[1.35]">
                  <div className="truncate font-semibold text-foreground">
                    {applicationTitle(a)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs text-muted-foreground">
                      +996 {fmtAppPhone(a.phone)}
                    </span>
                    {!a.phoneVerified && (
                      <span className="rounded-full bg-skr-warn/14 px-[7px] py-px text-[10.5px] font-semibold text-skr-warn">
                        не подтверждён
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </TableCell>
            <TableCell className="text-skr-ink2">{orDash(a.cityName)}</TableCell>
            <TableCell className="text-skr-ink2">{doctorsLabel(a.doctorsCount)}</TableCell>
            <TableCell className="font-mono text-[12.5px] text-skr-ink2">
              {fmtAppDate(a.createdAt)}
            </TableCell>
            <TableCell>
              <AppStatusPill status={a.status} />
            </TableCell>
            <TableCell className="text-[12.5px] text-muted-foreground">
              {a.reviewedByName ?? '—'}
            </TableCell>
            <TableCell className="pr-2">
              <ChevronRight className="size-4 text-skr-chevron" strokeWidth={2} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
